import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";
import { IMAGES } from "./data";
import { EASE } from "./Reveal";
import { useGo } from "@/lib/nav";

const LINES = ["Mary, Mother", "of the Church"];

const MaskedLine = ({ text, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);
  const go = useGo();

  return (
    <section id="home" data-testid="hero-section" ref={ref} className="relative overflow-hidden">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <div
        className="pointer-events-none absolute -top-40 right-[-15%] h-[34rem] w-[34rem] rounded-full bg-paleblue/60 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-20 pt-14 sm:px-8 sm:pt-20 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-10 lg:px-16 lg:pb-28">
        <motion.div style={{ y: textY }} className="max-w-xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.34em] text-gold"
          >
            Theotokos · Mater Dei — A Catholic Treasury of Our Lady
          </motion.p>

          <h1 className="mt-6 font-serif text-5xl font-semibold leading-[1.02] tracking-tight text-midnight sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <MaskedLine key={line} text={line} delay={0.35 + i * 0.14} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-md text-lg leading-relaxed text-charcoal/85"
          >
            Explore a Catholic treasury of Scripture, prayer, Marian apparitions, shrines,
            feasts, saints, and devotion from around the world.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              data-testid="hero-cta-apparitions"
              onClick={() => go("/apparitions")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-marian px-7 py-3.5 text-sm font-semibold text-ivory shadow-[0_18px_36px_-16px_rgba(31,78,121,0.85)] transition-all duration-300 hover:bg-midnight hover:shadow-[0_22px_40px_-16px_rgba(16,42,67,0.9)]"
            >
              Explore the Apparitions
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              type="button"
              data-testid="hero-cta-rosary"
              onClick={() => go("/rosary")}
              className="group inline-flex items-center gap-2.5 rounded-full border border-marian/35 bg-white/70 px-7 py-3.5 text-sm font-semibold text-marian backdrop-blur transition-all duration-300 hover:border-gold hover:bg-white hover:text-midnight"
            >
              <Flame className="h-4 w-4 text-gold transition-transform duration-300 group-hover:scale-110" />
              Pray the Rosary
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-10 font-serif text-base italic text-slateblue"
          >
            All Marian devotion finds its meaning in Christ, the one Saviour and the heart of
            the Gospel.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.55, ease: EASE }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <motion.div style={{ y: artY }} className="relative">
            <motion.div
              style={{ opacity: glowOpacity }}
              className="absolute -inset-8 rounded-full bg-[radial-gradient(circle_at_50%_38%,rgba(184,134,11,0.22),transparent_65%)] blur-2xl"
              aria-hidden="true"
            />
            <figure className="arch-frame relative overflow-hidden border border-gold/40 bg-white shadow-[0_50px_90px_-40px_rgba(16,42,67,0.55)]">
              <div className="arch-frame absolute inset-2 z-10 border border-white/60" aria-hidden="true" />
              <img
                src={IMAGES.hero.url}
                alt={IMAGES.hero.alt}
                loading="eager"
                className="aspect-[3/4] w-full scale-[1.06] object-cover object-top"
              />
              <div
                className="absolute inset-0 bg-[linear-gradient(to_top,rgba(16,42,67,0.32),transparent_38%)]"
                aria-hidden="true"
              />
            </figure>
            <figcaption className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-slateblue">
              {IMAGES.hero.credit}
            </figcaption>
          </motion.div>
        </motion.div>
      </div>

      <div className="gold-rule mx-auto max-w-5xl opacity-60" aria-hidden="true" />
    </section>
  );
}
